/**
 * ShareButtons — Social-share row for blog articles.
 *
 * Channels (in order): WhatsApp, Messenger (mobile-only fallback), Facebook,
 * Copy link, Threads. Each click dispatches a `share-click` CustomEvent on
 * window with `{ detail: { channel } }` for downstream telemetry.
 */

import { useCallback, useState } from 'react';
import { motion } from 'framer-motion';

export interface ShareButtonsProps {
  url: string;
  title: string;
  lang: 'no' | 'en';
}

type ShareChannel = 'whatsapp' | 'messenger' | 'facebook' | 'copy' | 'threads';

function dispatchShareClick(channel: ShareChannel) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('share-click', { detail: { channel } }));
  } catch {
    /* ignore */
  }
}

function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

export function ShareButtons({ url, title, lang }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const heading = lang === 'no'
    ? 'Del med en annen nysgjerrig forelder'
    : 'Share with another curious parent';

  const labels = lang === 'no'
    ? { whatsapp: 'WhatsApp', messenger: 'Messenger', facebook: 'Facebook', copy: 'Kopier lenke', copied: 'Kopiert!', threads: 'Threads' }
    : { whatsapp: 'WhatsApp', messenger: 'Messenger', facebook: 'Facebook', copy: 'Copy link', copied: 'Copied!', threads: 'Threads' };

  const encodedUrl = encodeURIComponent(url);
  const encodedShare = encodeURIComponent(`${title} ${url}`);

  const onCopy = useCallback(async () => {
    dispatchShareClick('copy');
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers — fall back to a transient textarea
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [url]);

  const showMessenger = isMobileUA();
  const messengerHref = `fb-messenger://share?link=${encodedUrl}`;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="max-w-3xl mx-auto my-10"
      aria-label={heading}
    >
      <p className="font-display font-semibold text-ink-muted text-sm mb-3 text-center">
        {heading}
      </p>
      <div className="flex flex-wrap gap-2 justify-center">
        <ShareLink
          href={`https://wa.me/?text=${encodedShare}`}
          label={labels.whatsapp}
          channel="whatsapp"
          bg="#25D366"
        />
        {showMessenger && (
          <ShareLink
            href={messengerHref}
            label={labels.messenger}
            channel="messenger"
            bg="#0084FF"
          />
        )}
        <ShareLink
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          label={labels.facebook}
          channel="facebook"
          bg="#1877F2"
        />
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-display font-medium text-sm bg-white text-ink border border-gray-200 hover:border-teal hover:text-teal transition-colors"
          aria-live="polite"
        >
          {copied ? labels.copied : labels.copy}
        </button>
        <ShareLink
          href={`https://threads.net/intent/post?text=${encodedShare}`}
          label={labels.threads}
          channel="threads"
          bg="#000000"
        />
      </div>
    </motion.section>
  );
}

interface ShareLinkProps {
  href: string;
  label: string;
  channel: ShareChannel;
  bg: string;
}

function ShareLink({ href, label, channel, bg }: ShareLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => dispatchShareClick(channel)}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-display font-medium text-sm text-white hover:opacity-90 transition-opacity"
      style={{ backgroundColor: bg }}
    >
      {label}
    </a>
  );
}

export default ShareButtons;
